import { useReducer } from "react"
// Side-effect import: registers the `useEvent` decoration on event definitions so
// `AmendsChanged(draftId).useEvent(...)` is available. The broader app loads this early in
// production; importing it here makes the hook self-contained for isolated tests.
import "lib/@hooks/useEvent"
import { useScheduleEditorIds } from "./use-schedule-editor-ids"
import { readScheduleEditorSession } from "../lib/schedule-editor-session"
import { AmendsChanged } from "../schedule-editor-event-definitions.mjs"
import { buildMergedAmends } from "../lib/build-merged-amends"

/**
 * Returns the live amends arrays for the current schedule editor.
 *
 * Subscribes to AmendsChanged(draftId) so a local re-render fires when an amend
 * is added, updated, or removed. The returned arrays are the SAME mutable
 * instances that useDraftAmends mutates in place, so reading them after the
 * event always sees the latest contents.
 *
 * Replaces the previous pattern of having the parent (ScheduleEditor) own
 * amends state and re-render to propagate it down — now only this hook's
 * caller re-renders.
 *
 * @returns {{ amends: Array, persistedAmends: Array, inheritedAmends: Array, allAmends: Array }}
 */
export function useAmendsValue() {
    const { draftId } = useScheduleEditorIds()
    const [, forceUpdate] = useReducer(tick, 0)

    AmendsChanged(draftId).useEvent(forceUpdate)

    const session = readScheduleEditorSession(draftId)
    const safePersisted = session?.persistedAmends ?? []
    const safeInherited = session?.inheritedAmends ?? []
    const mergedAmends = buildMergedAmends({ persistedAmends: safePersisted, inheritedAmends: safeInherited })

    return {
        amends: mergedAmends,
        persistedAmends: safePersisted,
        inheritedAmends: safeInherited,
        allAmends: [...safePersisted, ...safeInherited],
    }
}

function tick(n) {
    return n + 1
}
